<script setup>
import { computed } from 'vue'
import Layout from '@pages/Shared/layouts/StaffLayout.vue'
import Card from '@pages/Shared/Card.vue'
import { PhBuilding, PhStudent, PhBank, PhUserCircle, PhCalendarCheck, PhWarning, PhTrophy, PhClockCountdown, PhArrowUp, PhArrowDown } from '@phosphor-icons/vue'
import { Bar, Line, Doughnut } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import { usePage } from '@inertiajs/vue3'

/* ================= PROPS ================= */
const props = defineProps({
  statistics:   Object,
  sections:     Array,
  // Form master extras (optional – backend may not send all)
  attendance:   Object,   // { today: { present, absent, late }, weekly: [{day, present, absent}] }
  assessments:  Array,    // [{ subject, average, highest, lowest, submitted, total }]
  announcements: Array,   // [{ id, title, date, priority }]
  upcomingEvents: Array,  // [{ id, title, date, type }]
})

/* ================= CHART REGISTRATION ================= */
ChartJS.register(
  CategoryScale, LinearScale,
  BarElement, LineElement, ArcElement, PointElement,
  Tooltip, Legend, Filler
)

/* ================= SUMMARY COUNTS ================= */
const staffCount   = computed(() => props.statistics?.staff   ?? 0)
const studentCount = computed(() => props.statistics?.students ?? 0)
const sectionCount = computed(() => props.statistics?.sections ?? 0)
const classCount   = computed(() => props.statistics?.classes  ?? 0)

/* ================= PAGE & USER ================= */
const page          = usePage()
const user          = computed(() => page.props?.auth?.user || {})
const can           = computed(() => page.props?.can || {})
const userClassRoom = computed(() => user.value?.class_room ?? null)

/* ================= CLASS STATS ================= */
const students       = computed(() => userClassRoom.value?.students ?? [])
const totalStudents  = computed(() => students.value.length)
const maleStudents   = computed(() => students.value.filter(s => s.gender === 'M').length)
const femaleStudents = computed(() => students.value.filter(s => s.gender === 'F').length)
const averageAge     = computed(() => {
  if (!students.value.length) return 0
  return Math.round(students.value.reduce((acc, s) => acc + (s.age ?? 0), 0) / students.value.length)
})

// Determine if class is single-gender
const isMaleOnly   = computed(() => totalStudents.value > 0 && femaleStudents.value === 0)
const isFemaleOnly = computed(() => totalStudents.value > 0 && maleStudents.value === 0)
const isMixed      = computed(() => !isMaleOnly.value && !isFemaleOnly.value)

const genderRatio = computed(() => {
  const total = totalStudents.value
  if (!total) return { male: 0, female: 0 }
  return {
    male:   Math.round((maleStudents.value   / total) * 100),
    female: Math.round((femaleStudents.value / total) * 100),
  }
})

/* ================= ATTENDANCE ================= */
const todayPresent  = computed(() => props.attendance?.today?.present  ?? 0)
const todayAbsent   = computed(() => props.attendance?.today?.absent   ?? 0)
const todayLate     = computed(() => props.attendance?.today?.late     ?? 0)
const attendanceRate = computed(() => {
  const total = todayPresent.value + todayAbsent.value + todayLate.value
  return total ? Math.round(((todayPresent.value + todayLate.value) / total) * 100) : 0
})

const weeklyAttendance = computed(() => props.attendance?.weekly ?? [])

/* ================= ASSESSMENTS ================= */
const assessmentData = computed(() => props.assessments ?? [])
const classAverage   = computed(() => {
  if (!assessmentData.value.length) return 0
  return Math.round(assessmentData.value.reduce((s, a) => s + (a.average ?? 0), 0) / assessmentData.value.length)
})

/* ================= BASE CHART OPTIONS ================= */
const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#111827',
      padding: 12,
      titleFont: { size: 13 },
      bodyFont:  { size: 13 },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid:  { color: '#f3f4f6' },
      ticks: { color: '#9ca3af' },
    },
    x: {
      grid:  { display: false },
      ticks: { color: '#9ca3af' },
    },
  },
}

/* ================= SECTION BAR CHART ================= */
const sectionChart = computed(() => ({
  labels: props.sections?.map(s => s.name) ?? [],
  datasets: [{
    label: 'Students',
    data: props.sections?.map(s =>
      s.classes?.reduce((sum, c) => sum + Number(c.students_count ?? 0), 0) || 0
    ) ?? [],
    backgroundColor: [
      'rgba(34,197,94,0.85)',
      'rgba(59,130,246,0.85)',
      'rgba(168,85,247,0.85)',
      'rgba(234,179,8,0.85)'
    ],
    borderRadius: 10,
    barThickness: 42,
  }],
}))

/* ================= CLASS LINE CHART ================= */
const classChart = computed(() => {
  const classes = props.sections?.flatMap(s => s.classes || []) ?? []
  return {
    labels: classes.map(c => c.name),
    datasets: [{
      label: 'Students',
      data: classes.map(c => Number(c.students_count ?? 0)),
      borderColor: '#22c55e',
      backgroundColor: 'rgba(34,197,94,0.12)',
      fill: true,
      tension: 0.45,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#22c55e',
    }],
  }
})

/* ================= GENDER DOUGHNUT ================= */
const genderDoughnut = computed(() => ({
  labels: ['Male', 'Female'],
  datasets: [{
    data: [maleStudents.value, femaleStudents.value],
    backgroundColor: ['rgba(59,130,246,0.85)', 'rgba(236,72,153,0.85)'],
    borderWidth: 0,
    hoverOffset: 6,
  }],
}))

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '70%',
  plugins: {
    legend: { display: true, position: 'bottom', labels: { color: '#6b7280', font: { size: 12 } } },
    tooltip: { backgroundColor: '#111827', padding: 10 },
  },
}

/* ================= WEEKLY ATTENDANCE CHART ================= */
const attendanceChart = computed(() => ({
  labels: weeklyAttendance.value.map(d => d.day),
  datasets: [
    {
      label: 'Present',
      data: weeklyAttendance.value.map(d => d.present),
      backgroundColor: 'rgba(34,197,94,0.85)',
      borderRadius: 8,
      barThickness: 18,
    },
    {
      label: 'Absent',
      data: weeklyAttendance.value.map(d => d.absent),
      backgroundColor: 'rgba(239,68,68,0.75)',
      borderRadius: 8,
      barThickness: 18,
    },
  ],
}))

const attendanceChartOptions = {
  ...baseOptions,
  plugins: {
    ...baseOptions.plugins,
    legend: { display: true, position: 'top', labels: { color: '#6b7280', font: { size: 12 } } },
  },
}

/* ================= ASSESSMENT CHART ================= */
const assessmentChart = computed(() => ({
  labels: assessmentData.value.map(a => a.subject),
  datasets: [{
    label: 'Class Average (%)',
    data: assessmentData.value.map(a => a.average ?? 0),
    backgroundColor: assessmentData.value.map(a =>
      (a.average ?? 0) >= 70 ? 'rgba(34,197,94,0.85)' :
      (a.average ?? 0) >= 50 ? 'rgba(234,179,8,0.85)' :
      'rgba(239,68,68,0.75)'
    ),
    borderRadius: 10,
    barThickness: 32,
  }],
}))

const assessmentChartOptions = {
  ...baseOptions,
  scales: {
    ...baseOptions.scales,
    y: { ...baseOptions.scales.y, max: 100, ticks: { ...baseOptions.scales.y.ticks, callback: v => v + '%' } },
  },
}

/* ================= CHART SHARED OPTIONS (section/class) ================= */
const sectionChartOptions = { ...baseOptions }
const classChartOptions   = {
  ...baseOptions,
  scales: {
    ...baseOptions.scales,
    x: {
      grid: { display: false },
      ticks: { color: '#9ca3af', maxRotation: 45, minRotation: 45, autoSkip: false, padding: 10 },
    },
  },
}
</script>

<template>
  <Layout>

    <div class="flex items-center justify-between mb-6">
      <h2 class="text-2xl font-semibold text-primary">Dashboard</h2>
    </div>

    <!-- ===================== FULL ADMIN DASHBOARD ===================== -->
    <template v-if="can.viewDashboard">

      <div class="grid gap-6 mb-8 md:grid-cols-2 xl:grid-cols-4">
        <Card title="Staff"    :subtitle="staffCount"   color="bg-orange-100"><PhUserCircle :size="32" class="text-orange-500" /></Card>
        <Card title="Students" :subtitle="studentCount" color="bg-green-100"><PhStudent    :size="32" class="text-green-600"  /></Card>
        <Card title="Sections" :subtitle="sectionCount" color="bg-primary-100"><PhBuilding   :size="32" class="text-primary-500"   /></Card>
        <Card title="Classes"  :subtitle="classCount"   color="bg-purple-100"><PhBank      :size="32" class="text-purple-500" /></Card>
      </div>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 class="text-base font-semibold text-gray-700 mb-1">Students by Section</h3>
          <p class="text-sm text-gray-400 mb-4">Enrollment across academic programs</p>
          <div class="h-[260px]">
            <Bar v-if="sectionChart.labels.length" :data="sectionChart" :options="sectionChartOptions" />
            <div v-else class="flex items-center justify-center h-full text-gray-400 text-sm">No data</div>
          </div>
        </div>

        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:col-span-2">
          <h3 class="text-base font-semibold text-gray-700 mb-1">Class Enrollment Overview</h3>
          <p class="text-sm text-gray-400 mb-4">Student population per class</p>
          <div class="overflow-x-auto">
            <div class="h-[260px] min-w-full md:min-w-[700px]">
              <Line v-if="classChart.labels.length" :data="classChart" :options="classChartOptions" />
              <div v-else class="flex items-center justify-center h-full text-gray-400 text-sm">No data</div>
            </div>
          </div>
        </div>
      </div>

    </template>

    <!-- ===================== FORM MASTER / CLASS TEACHER VIEW ===================== -->
    <template v-else>

      <!-- Class header -->
      <div class="mb-6 flex items-start gap-3">
        <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <PhBank :size="22" class="text-primary" />
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-800">{{ userClassRoom?.name ?? 'Your Class' }}</h3>
          <p class="text-sm text-gray-500">Form Master: {{ user.name }}
            <span v-if="isMaleOnly"   class="ml-2 text-xs bg-primary-100 text-primary-600 px-2 py-0.5 rounded-full font-medium">Boys Only</span>
            <span v-if="isFemaleOnly" class="ml-2 text-xs bg-pink-100 text-pink-600 px-2 py-0.5 rounded-full font-medium">Girls Only</span>
            <span v-if="isMixed"      class="ml-2 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">Mixed</span>
          </p>
        </div>
      </div>

      <!-- Summary cards -->
      <div class="grid gap-5 mb-8 grid-cols-2 xl:grid-cols-4">
        <Card title="Total Students" :subtitle="totalStudents"  color="bg-green-100"><PhStudent      :size="32" class="text-green-600"  /></Card>
        <Card title="Present Today"  :subtitle="todayPresent"   color="bg-primary-100"><PhCalendarCheck :size="32" class="text-primary-500"   /></Card>
        <Card title="Absent Today"   :subtitle="todayAbsent"    color="bg-red-100"><PhWarning        :size="32" class="text-red-500"    /></Card>
        <Card title="Class Average"  :subtitle="classAverage + '%'" color="bg-purple-100"><PhTrophy :size="32" class="text-purple-500" /></Card>
      </div>

      <!-- Row 1: Attendance + Gender -->
      <div class="grid grid-cols-1 gap-6 md:grid-cols-3 mb-6">

        <!-- Weekly Attendance Chart -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:col-span-2">
          <div class="flex items-center justify-between mb-1">
            <h3 class="text-base font-semibold text-gray-700">Weekly Attendance</h3>
            <span class="text-sm font-semibold" :class="attendanceRate >= 80 ? 'text-green-600' : attendanceRate >= 60 ? 'text-yellow-500' : 'text-red-500'">
              {{ attendanceRate }}% today
            </span>
          </div>
          <p class="text-sm text-gray-400 mb-4">Present vs absent this week</p>
          <div class="h-[220px]">
            <Bar v-if="weeklyAttendance.length" :data="attendanceChart" :options="attendanceChartOptions" />
            <div v-else class="flex items-center justify-center h-full text-gray-400 text-sm">No attendance data recorded</div>
          </div>
        </div>

        <!-- Gender Breakdown -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 class="text-base font-semibold text-gray-700 mb-1">Gender Breakdown</h3>
          <p class="text-sm text-gray-400 mb-4">
            <template v-if="isMixed">{{ genderRatio.male }}% M · {{ genderRatio.female }}% F</template>
            <template v-else-if="isMaleOnly">All male class</template>
            <template v-else>All female class</template>
          </p>
          <div class="h-[180px]">
            <Doughnut v-if="totalStudents" :data="genderDoughnut" :options="doughnutOptions" />
            <div v-else class="flex items-center justify-center h-full text-gray-400 text-sm">No students enrolled</div>
          </div>
          <div class="mt-4 grid grid-cols-2 gap-3 text-center" v-if="isMixed">
            <div class="bg-primary-50 rounded-lg py-2">
              <p class="text-lg font-bold text-primary-600">{{ maleStudents }}</p>
              <p class="text-xs text-gray-500">Male</p>
            </div>
            <div class="bg-pink-50 rounded-lg py-2">
              <p class="text-lg font-bold text-pink-500">{{ femaleStudents }}</p>
              <p class="text-xs text-gray-500">Female</p>
            </div>
          </div>
          <div class="mt-4" v-else>
            <div class="rounded-lg py-2 text-center" :class="isMaleOnly ? 'bg-primary-50' : 'bg-pink-50'">
              <p class="text-2xl font-bold" :class="isMaleOnly ? 'text-primary-600' : 'text-pink-500'">{{ totalStudents }}</p>
              <p class="text-xs text-gray-500">{{ isMaleOnly ? 'Male Students' : 'Female Students' }}</p>
            </div>
          </div>
        </div>

      </div>

      <!-- Row 2: Assessment Performance + Announcements + Upcoming -->
      <div class="grid grid-cols-1 gap-6 md:grid-cols-3">

        <!-- Assessment Performance Chart -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:col-span-2">
          <div class="flex items-center justify-between mb-1">
            <h3 class="text-base font-semibold text-gray-700">Assessment Performance</h3>
            <span class="text-xs text-gray-400">Class average per subject</span>
          </div>
          <p class="text-sm text-gray-400 mb-4">Green ≥ 70% · Yellow ≥ 50% · Red &lt; 50%</p>
          <div class="h-[220px]">
            <Bar v-if="assessmentData.length" :data="assessmentChart" :options="assessmentChartOptions" />
            <div v-else class="flex items-center justify-center h-full text-gray-400 text-sm">No assessment data yet</div>
          </div>

          <!-- Subject summary table -->
          <div v-if="assessmentData.length" class="mt-4 overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-gray-400 text-xs border-b border-gray-100">
                  <th class="pb-2 font-medium">Subject</th>
                  <th class="pb-2 font-medium text-center">Avg</th>
                  <th class="pb-2 font-medium text-center">High</th>
                  <th class="pb-2 font-medium text-center">Low</th>
                  <th class="pb-2 font-medium text-center">Submitted</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="a in assessmentData" :key="a.subject" class="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td class="py-2 text-gray-700 font-medium">{{ a.subject }}</td>
                  <td class="py-2 text-center font-semibold" :class="(a.average ?? 0) >= 70 ? 'text-green-600' : (a.average ?? 0) >= 50 ? 'text-yellow-500' : 'text-red-500'">
                    {{ a.average ?? '—' }}%
                  </td>
                  <td class="py-2 text-center text-gray-500">{{ a.highest ?? '—' }}</td>
                  <td class="py-2 text-center text-gray-500">{{ a.lowest ?? '—' }}</td>
                  <td class="py-2 text-center text-gray-500">{{ a.submitted ?? 0 }}/{{ a.total ?? totalStudents }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Right column: Announcements + Upcoming Events -->
        <div class="flex flex-col gap-6">

          <!-- Announcements -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex-1">
            <h3 class="text-base font-semibold text-gray-700 mb-4">Announcements</h3>
            <ul v-if="announcements?.length" class="flex flex-col gap-3">
              <li v-for="a in announcements" :key="a.id" class="flex items-start gap-2">
                <span class="mt-0.5 w-2 h-2 rounded-full shrink-0" :class="a.priority === 'high' ? 'bg-red-400' : a.priority === 'medium' ? 'bg-yellow-400' : 'bg-green-400'" />
                <div>
                  <p class="text-sm text-gray-700 font-medium leading-snug">{{ a.title }}</p>
                  <p class="text-xs text-gray-400 mt-0.5">{{ a.date }}</p>
                </div>
              </li>
            </ul>
            <p v-else class="text-sm text-gray-400">No announcements</p>
          </div>

          <!-- Upcoming Events -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex-1">
            <h3 class="text-base font-semibold text-gray-700 mb-4">Upcoming Events</h3>
            <ul v-if="upcomingEvents?.length" class="flex flex-col gap-3">
              <li v-for="e in upcomingEvents" :key="e.id" class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  :class="e.type === 'exam' ? 'bg-red-100' : e.type === 'holiday' ? 'bg-green-100' : 'bg-primary-100'">
                  <PhClockCountdown :size="18"
                    :class="e.type === 'exam' ? 'text-red-500' : e.type === 'holiday' ? 'text-green-600' : 'text-primary-500'" />
                </div>
                <div class="min-w-0">
                  <p class="text-sm text-gray-700 font-medium truncate">{{ e.title }}</p>
                  <p class="text-xs text-gray-400">{{ e.date }}</p>
                </div>
              </li>
            </ul>
            <p v-else class="text-sm text-gray-400">No upcoming events</p>
          </div>

        </div>
      </div>

    </template>

  </Layout>
</template>